import React from 'react';
import PropTypes from 'prop-types';
import './Button.css';

/**
 * Button component with consistent styling and spacing
 * 
 * @param {Object} props - Component props
 * @param {ReactNode} props.children - Button content
 * @param {string} props.variant - Button style variant (solid or outline)
 * @param {string} props.color - Button color scheme (navy or cream)
 * @param {string} props.className - Additional CSS classes
 * @param {ReactNode} props.icon - Icon to display on the right
 * @param {string} props.href - URL for anchor buttons
 * @param {string} props.type - Button type (button, submit, reset)
 * @param {boolean} props.disabled - Whether button is disabled
 * @param {function} props.onClick - Click handler
 * @returns {JSX.Element} - Button component
 */
const Button = ({ 
  children, 
  variant = 'solid', 
  color = 'navy',
  className = '', 
  icon,
  href,
  to,
  as,
  type = 'button',
  disabled = false,
  onClick,
  ...props 
}) => {
  // Automatically determine component type based on props
  let Component = as;
  if (!Component) {
    if (href) {
      Component = 'a';
    } else if (to) {
      Component = 'Link'; // Would need React Router Link imported
    } else {
      Component = 'button';
    }
  }
  
  // Base classes for all buttons - keep rounded-md
  const baseClasses = 'inline-flex items-center justify-center rounded-md font-medium transition-all duration-300 text-base border-2 btn-animated';
  
  // Padding classes - consistent for all buttons
  const paddingClasses = 'px-6 py-3';
  
  // Icon spacing - increased spacing between text and icon
  const iconSpacingClass = icon ? 'space-x-4' : '';
  
  // Style variations based on variant and color
  const variantClasses = {
    // Solid buttons with border for consistent height
    'solid-navy': 'btn-solid-navy',
    'solid-cream': 'btn-solid-cream',
    
    // Outline buttons
    'outline-navy': 'btn-outline-navy',
    'outline-cream': 'btn-outline-cream',
    
    // Glass effect buttons
    'glass-navy': 'btn-glass-navy',
    'glass-cream': 'btn-glass-cream',
    'glass-white': 'btn-glass-white',
  };
  
  // Disabled state
  const disabledClasses = disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer';
  
  // Combine all classes
  const buttonClasses = `${baseClasses} ${paddingClasses} ${iconSpacingClass} ${variantClasses[`${variant}-${color}`] || variantClasses['solid-navy']} ${disabledClasses} ${className}`;
  
  // Handle props based on component type
  const componentProps = {};
  
  // Standard button props
  if (Component === 'button') {
    componentProps.type = type;
    componentProps.disabled = disabled;
  } 
  // Anchor props
  else if (Component === 'a') {
    componentProps.href = disabled ? undefined : href;
  } 
  // React Router Link props or custom component
  else {
    componentProps.to = to;
    componentProps.disabled = disabled;
  }
  
  return (
    <Component
      className={buttonClasses}
      onClick={disabled ? undefined : onClick}
      {...componentProps}
      {...props}
    >
      <span>{children}</span>
      {icon && <span className="flex-shrink-0">{icon}</span>}
    </Component>
  );
};

Button.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(['solid', 'outline', 'glass']),
  color: PropTypes.oneOf(['navy', 'cream', 'white']),
  className: PropTypes.string,
  icon: PropTypes.node,
  href: PropTypes.string,
  to: PropTypes.string,
  as: PropTypes.elementType,
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
};

export default Button;